// App-level settings (Phase 7).
//
// Currently a single field: the user's dark-mode preference. Stored as part
// of PersistedState v3 (see persistence.ts). Applied to the document by
// flipping the `data-theme` attribute on <html>; the CSS in styles/index.css
// reads the attribute via :root[data-theme="..."] selectors that take
// specificity precedence over the @media (prefers-color-scheme) defaults.
//
// Pure module: no DOM-event wiring (that lives in ui/settingsOverlay.ts) and
// no persistence calls (those live in persistence.ts). The applySettings
// helper does touch the document but is idempotent + injectable.

export type DarkModePreference = 'auto' | 'light' | 'dark'

export interface AppSettings {
  readonly darkMode: DarkModePreference
}

// First-visit default is 'light' — existing users' persisted darkMode is
// always loaded by persistence.ts and overrides this default. The settings
// dialog still offers all three options (Auto / Light / Dark).
export const DEFAULT_SETTINGS: AppSettings = { darkMode: 'light' } as const

// Apply the chosen theme by setting (or removing) the data-theme attribute on
// the document root. Idempotent. `doc` defaults to the live document so
// callers don't need to thread it in production; tests can pass a fresh
// document for isolation.
export function applySettings(settings: AppSettings, doc: Document = document): void {
  const root = doc.documentElement
  if (settings.darkMode === 'auto') {
    root.removeAttribute('data-theme')
    return
  }
  root.setAttribute('data-theme', settings.darkMode)
}

// Runtime narrowing guard. Used by persistence.ts on the v3 parse path.
export function isValidSettings(v: unknown): v is AppSettings {
  if (v === null || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  const dm = o['darkMode']
  if (dm !== 'auto' && dm !== 'light' && dm !== 'dark') return false
  return true
}
